import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { track } from "@/lib/analytics";
import { Mail, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  pt: {
    badge: "Newsletter semanal · grátis",
    title: "Um e-mail por semana. Zero spam. Muito francês.",
    body: "Toda quinta-feira o Prof Lionel manda 1 dica curta de gramática, cultura ou vocabulário — direto para o seu inbox, escrita para brasileiros aprendendo francês.",
    nameLabel: "Como devo te chamar?",
    namePh: "Mariana",
    emailLabel: "Seu melhor e-mail",
    emailPh: "voce@email.com",
    interestsLabel: "Quero receber conteúdo sobre:",
    interests: [
      { id: "gramatica", label: "Gramática" },
      { id: "cultura", label: "Cultura & viagem" },
      { id: "provas", label: "Provas (DELF/DALF/TEF)" },
    ],
    cta: "Quero receber",
    ctaLoading: "Enviando…",
    micro: "Você pode cancelar em qualquer e-mail com 1 clique. Nunca compartilhamos.",
    success: "Prontinho, {name}! Confere seu inbox pra confirmar 💌",
    successFallback: "Prontinho! Confere seu inbox 💌",
    errName: "Coloca seu apelido, por favor.",
    errEmail: "E-mail inválido.",
    errGeneric: "Não deu certo desta vez. Tenta de novo?",
  },
  en: {
    badge: "Weekly newsletter · free",
    title: "One email a week. Zero spam. Loads of French.",
    body: "Every Thursday Prof Lionel sends a short tip on grammar, culture or vocabulary — straight to your inbox, written for Brazilians learning French.",
    nameLabel: "What should I call you?",
    namePh: "Mariana",
    emailLabel: "Your best email",
    emailPh: "you@email.com",
    interestsLabel: "I want content on:",
    interests: [
      { id: "gramatica", label: "Grammar" },
      { id: "cultura", label: "Culture & travel" },
      { id: "provas", label: "Exams (DELF/DALF/TEF)" },
    ],
    cta: "Subscribe",
    ctaLoading: "Sending…",
    micro: "One-click unsubscribe on any email. We never share your address.",
    success: "You're in, {name}! Check your inbox 💌",
    successFallback: "You're in! Check your inbox 💌",
    errName: "Please tell me your name.",
    errEmail: "Invalid email.",
    errGeneric: "Something failed. Try again?",
  },
  fr: {
    badge: "Newsletter hebdomadaire · gratuite",
    title: "Un mail par semaine. Zéro spam. Plein de français.",
    body: "Chaque jeudi, le Prof Lionel envoie une astuce courte de grammaire, culture ou vocabulaire — directement dans votre boîte, écrite pour les Brésiliens qui apprennent le français.",
    nameLabel: "Comment vous appeler ?",
    namePh: "Mariana",
    emailLabel: "Votre meilleur e-mail",
    emailPh: "vous@email.com",
    interestsLabel: "Je veux du contenu sur :",
    interests: [
      { id: "gramatica", label: "Grammaire" },
      { id: "cultura", label: "Culture & voyage" },
      { id: "provas", label: "Examens (DELF/DALF/TEF)" },
    ],
    cta: "Je m'abonne",
    ctaLoading: "Envoi…",
    micro: "Désabonnement en 1 clic dans chaque e-mail. Nous ne partageons jamais.",
    success: "C'est fait, {name} ! Vérifiez votre boîte 💌",
    successFallback: "C'est fait ! Vérifiez votre boîte 💌",
    errName: "Dites-moi comment vous appeler.",
    errEmail: "E-mail invalide.",
    errGeneric: "Ça n'a pas marché. On réessaie ?",
  },
};

const Newsletter = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]); // ids of checked topics
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);

  const isValidEmail = (v) => /.+@.+\..+/.test(v);

  const toggleInterest = (id) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t.errName);
      return;
    }
    if (!isValidEmail(email)) {
      toast.error(t.errEmail);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: trimmed,
        email: email.trim(),
        language: lang,
        source: "newsletter",
        interest: interests.length ? interests.join(",") : null,
        website, // honeypot
      });
      track("newsletter_subscribe_success", {
        language: lang,
        interests_count: interests.length,
        interests: interests.join(",") || "none",
      });
      const first = trimmed.split(" ")[0];
      toast.success(t.success.replace("{name}", first) || t.successFallback);
      setName("");
      setEmail("");
      setInterests([]);
    } catch (err) {
      track("newsletter_subscribe_error", { language: lang });
      toast.error(t.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="bg-[#1e2a5a] text-[#f4f1ea] py-20 sm:py-28 border-t border-[#2a3766] overflow-hidden relative"
    >
      {/* Decorative sparks — behind, no interactivity */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#d9381e]/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[#e5b3b3]/15 blur-3xl"
      />

      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/25 px-3 py-1 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-[#f4f1ea]/85">
          <Sparkles className="h-3 w-3 text-[#d9381e]" strokeWidth={2.5} />
          {t.badge}
        </div>
        <h2
          data-testid="newsletter-title"
          className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] max-w-3xl"
        >
          {t.title}
        </h2>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-[#f4f1ea]/80 leading-relaxed">
          {t.body}
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="newsletter-form"
          className="mt-10 grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]"
        >
          {/* honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="hidden"
            aria-hidden="true"
          />
          <label className="sr-only" htmlFor="newsletter-name">{t.nameLabel}</label>
          <input
            id="newsletter-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.namePh}
            data-testid="newsletter-name-input"
            className="rounded-full bg-[#f4f1ea]/8 border border-[#f4f1ea]/25 px-5 py-3 text-sm text-[#f4f1ea] placeholder:text-[#f4f1ea]/45 focus:bg-[#f4f1ea]/12 focus:border-[#d9381e] focus:outline-none transition-colors"
          />
          <label className="sr-only" htmlFor="newsletter-email">{t.emailLabel}</label>
          <div className="relative">
            <Mail
              aria-hidden="true"
              className="absolute left-5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#f4f1ea]/45"
              strokeWidth={1.75}
            />
            <input
              id="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.emailPh}
              data-testid="newsletter-email-input"
              className="w-full rounded-full bg-[#f4f1ea]/8 border border-[#f4f1ea]/25 pl-11 pr-5 py-3 text-sm text-[#f4f1ea] placeholder:text-[#f4f1ea]/45 focus:bg-[#f4f1ea]/12 focus:border-[#d9381e] focus:outline-none transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#d9381e] px-6 py-3 text-sm font-semibold text-[#f4f1ea] transition-colors hover:bg-[#f4f1ea] hover:text-[#1e2a5a] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t.ctaLoading : t.cta}
            <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
          </button>
        </form>

        {/* Interest checkboxes — segments future newsletter delivery */}
        <fieldset
          data-testid="newsletter-interests"
          className="mt-5 flex flex-wrap items-center gap-2"
        >
          <legend className="w-full mb-2 text-xs uppercase tracking-[0.18em] text-[#f4f1ea]/60">
            {t.interestsLabel}
          </legend>
          {t.interests.map((it) => {
            const active = interests.includes(it.id);
            return (
              <label
                key={it.id}
                data-testid={`newsletter-interest-${it.id}`}
                className={`cursor-pointer select-none inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs sm:text-sm transition-all border ${
                  active
                    ? "bg-[#d9381e] border-[#d9381e] text-[#f4f1ea]"
                    : "bg-transparent border-[#f4f1ea]/25 text-[#f4f1ea]/80 hover:border-[#f4f1ea]/60"
                }`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleInterest(it.id)}
                  className="sr-only"
                />
                <span className={`inline-block h-2 w-2 rounded-full ${active ? "bg-[#f4f1ea]" : "bg-[#f4f1ea]/40"}`} />
                {it.label}
              </label>
            );
          })}
        </fieldset>

        <p className="mt-5 text-xs text-[#f4f1ea]/55 max-w-2xl leading-relaxed">
          {t.micro}
        </p>
      </div>
    </section>
  );
};

export default Newsletter;
